import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Bell, CalendarDays, CalendarOff, CheckCircle2, ChevronLeft, ChevronRight, PenLine, Send } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtHora, hoyISO } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import RegistrarAusenciaDialog from "@/components/RegistrarAusenciaDialog";

const ESTADO_STYLE = {
  sin_fichaje: { label: "Sin fichaje", className: "border-rose-200 bg-rose-100 text-rose-800" },
  falta_entrada: { label: "Falta entrada", className: "border-orange-200 bg-orange-100 text-orange-800" },
  falta_salida: { label: "Falta salida", className: "border-amber-200 bg-amber-100 text-amber-800" },
  jornada_incompleta: { label: "Incompleta", className: "border-red-200 bg-red-100 text-red-800" },
  completo: { label: "Completo", className: "border-green-200 bg-green-100 text-green-800" },
  en_jornada: { label: "En jornada", className: "border-emerald-200 bg-emerald-100 text-emerald-800" },
  pendiente: { label: "Pendiente", className: "border-sky-200 bg-sky-100 text-sky-800" },
  parcial: { label: "Parcial", className: "border-zinc-200 bg-zinc-100 text-zinc-700" },
  ausencia: { label: "Ausencia", className: "border-indigo-200 bg-indigo-100 text-indigo-800" },
  permiso_justificado: { label: "Permiso justificado", className: "border-orange-200 bg-orange-100 text-orange-900" },
  no_laborable: { label: "No laborable", className: "border-zinc-200 bg-zinc-100 text-zinc-500" },
};

function moverDia(iso, delta) {
  const d = new Date(`${iso}T12:00:00`);
  d.setDate(d.getDate() + delta);
  return d.toLocaleDateString("sv");
}

export default function CumplimientoFichaje() {
  const [fecha, setFecha] = useState(hoyISO());
  const [data, setData] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [mensaje, setMensaje] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [enviarEmail, setEnviarEmail] = useState(true);
  const [soloIncidencias, setSoloIncidencias] = useState(true);
  const [ausenciaOpen, setAusenciaOpen] = useState(false);
  const [ausenciaUsuario, setAusenciaUsuario] = useState(null);
  const [asuntosPropiosActivo, setAsuntosPropiosActivo] = useState(false);
  const [permisosAprobacion, setPermisosAprobacion] = useState(null);

  const esHoy = fecha === hoyISO();

  const puedeRegistrarAlgunTipo = useMemo(() => {
    if (!permisosAprobacion) return false;
    const tiposRegistro = ["vacaciones", "baja", "asuntos_propios", "ausencia_justificada"];
    return tiposRegistro.some((id) => {
      if (id === "asuntos_propios" && !asuntosPropiosActivo) return false;
      return permisosAprobacion[id] !== false;
    });
  }, [permisosAprobacion, asuntosPropiosActivo]);

  const cargar = useCallback(async () => {
    try {
      const { data: d } = await api.get("/dashboard/cumplimiento", { params: { fecha } });
      setData(d);
      setSel(new Set(d.incidencias.map((i) => i.usuario_id)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [fecha]);

  useEffect(() => {
    api.get("/usuarios").then((r) => setUsuarios(r.data)).catch(() => {});
    api.get("/solicitudes/funcionalidades")
      .then((r) => {
        setAsuntosPropiosActivo(!!r.data?.asuntos_propios_activo);
        setPermisosAprobacion(r.data?.permisos_aprobacion ?? null);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    cargar();
    if (!esHoy) return undefined;
    const t = setInterval(cargar, 60000);
    return () => clearInterval(t);
  }, [cargar, esHoy]);

  const lista = useMemo(() => {
    if (!data) return [];
    return soloIncidencias ? data.incidencias : data.empleados;
  }, [data, soloIncidencias]);

  const toggle = (id) => {
    setSel((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };

  const toggleTodos = () => {
    const ids = lista.filter((i) => i.problemas?.length).map((i) => i.usuario_id);
    if (ids.every((id) => sel.has(id))) setSel(new Set());
    else setSel(new Set(ids.length ? ids : lista.map((i) => i.usuario_id)));
  };

  const enviarRecordatorios = async (idsOverride = null) => {
    const ids = idsOverride || [...sel];
    if (!ids.length) {
      toast.error("Selecciona al menos un trabajador");
      return;
    }
    setEnviando(true);
    try {
      const { data: r } = await api.post("/fichajes/recordatorios", {
        usuario_ids: ids,
        mensaje: mensaje.trim(),
        enviar_email: enviarEmail,
      });
      const extra = r.emails_enviados
        ? ` · ${r.emails_enviados} email(s) enviado(s)`
        : enviarEmail && r.emails_fallidos
          ? " · email no configurado o falló el envío"
          : "";
      toast.success(`Recordatorio enviado a ${r.enviados} trabajador(es)${extra}`);
      setMensaje("");
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  const abrirAusencia = (emp = null) => {
    setAusenciaUsuario(emp);
    setAusenciaOpen(true);
  };

  if (!data) {
    return (
      <div className="flex justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-rose-200 border-t-rose-600" />
      </div>
    );
  }

  return (
    <>
      <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm" data-testid="cumplimiento-fichaje">
        <div className="border-b border-rose-100 bg-gradient-to-r from-rose-50 to-orange-50 px-5 py-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="font-display flex items-center gap-2 text-lg font-medium text-rose-900">
                <AlertTriangle className="h-5 w-5" />
                Registro de jornada
              </h2>
              <p className="mt-1 text-sm text-rose-800/80">
                {data.resumen.con_incidencia} incidencia(s) · {data.resumen.laborables} con jornada
                {data.resumen.ausentes > 0 && ` · ${data.resumen.ausentes} ausente(s)`}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {puedeRegistrarAlgunTipo && (
              <Button type="button" size="sm" variant="outline" className="h-8 bg-white" onClick={() => abrirAusencia()}>
                <CalendarOff className="mr-1 h-3.5 w-3.5" />
                Registrar ausencia
              </Button>
              )}
              <label className="flex items-center gap-2 text-sm text-zinc-600">
                <Checkbox checked={soloIncidencias} onCheckedChange={(v) => setSoloIncidencias(!!v)} />
                Solo incidencias
              </label>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button type="button" size="icon" variant="outline" className="h-8 w-8 bg-white" onClick={() => setFecha(moverDia(fecha, -1))} aria-label="Día anterior">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-2 rounded-lg border border-rose-100 bg-white px-2 py-1">
              <CalendarDays className="h-4 w-4 text-rose-600" />
              <Input
                type="date"
                value={fecha}
                onChange={(e) => setFecha(e.target.value)}
                className="h-8 w-36 border-0 p-0 shadow-none focus-visible:ring-0"
                data-testid="cumplimiento-fecha-input"
              />
            </div>
            <Button type="button" size="icon" variant="outline" className="h-8 w-8 bg-white" onClick={() => setFecha(moverDia(fecha, 1))} aria-label="Día siguiente">
              <ChevronRight className="h-4 w-4" />
            </Button>
            {!esHoy && (
              <Button type="button" size="sm" variant="ghost" className="h-8 text-rose-700" onClick={() => setFecha(hoyISO())}>
                Hoy
              </Button>
            )}
            <span className="text-sm font-medium text-rose-900">{fmtFecha(data.fecha)}</span>
          </div>
        </div>

        {lista.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-5 py-10 text-center">
            <CheckCircle2 className="h-10 w-10 text-emerald-500" />
            <p className="text-sm font-medium text-zinc-700">
              {soloIncidencias ? "Sin incidencias de fichaje este día" : "Sin empleados con jornada este día"}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-zinc-100 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                  <th className="px-4 py-2 w-10">
                    <Checkbox
                      checked={lista.length > 0 && lista.every((i) => sel.has(i.usuario_id))}
                      onCheckedChange={toggleTodos}
                    />
                  </th>
                  <th className="px-2 py-2">Empleado</th>
                  <th className="px-2 py-2 hidden md:table-cell">Horario</th>
                  <th className="px-2 py-2">Estado</th>
                  <th className="px-2 py-2 hidden sm:table-cell">Entrada / Salida</th>
                  <th className="px-2 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((emp) => {
                  const st = ESTADO_STYLE[emp.estado] || ESTADO_STYLE.parcial;
                  const esAusencia = emp.estado === "ausencia";
                  const esNoLaborable = emp.estado === "no_laborable";
                  return (
                    <tr key={emp.usuario_id} className="border-b border-zinc-50 hover:bg-zinc-50/80">
                      <td className="px-4 py-2.5">
                        {!esAusencia && !esNoLaborable && (
                          <Checkbox checked={sel.has(emp.usuario_id)} onCheckedChange={() => toggle(emp.usuario_id)} />
                        )}
                      </td>
                      <td className="px-2 py-2.5">
                        <p className="font-medium text-zinc-900">{emp.nombre}</p>
                        <p className="text-xs text-zinc-400">{emp.departamento || "—"}</p>
                        <p className="mt-0.5 text-xs text-zinc-500 md:hidden">{emp.horario_previsto}</p>
                      </td>
                      <td className="px-2 py-2.5 text-zinc-600 hidden md:table-cell">{emp.horario_previsto}</td>
                      <td className="px-2 py-2.5">
                        <Badge variant="outline" className={st.className}>{st.label}</Badge>
                        {emp.incidencias_tramos?.length > 0 && (
                          <ul className="mt-1.5 max-w-md space-y-0.5 text-xs text-rose-700">
                            {emp.incidencias_tramos.map((inc, i) => (
                              <li key={i}>· {inc.detalle}</li>
                            ))}
                          </ul>
                        )}
                        {emp.detalle && !emp.incidencias_tramos?.length && (
                          <p className="mt-1 max-w-xs text-xs text-zinc-500">{emp.detalle}</p>
                        )}
                      </td>
                      <td className="px-2 py-2.5 text-xs text-zinc-500 hidden sm:table-cell">
                        {emp.hora_entrada ? fmtHora(emp.hora_entrada) : "—"}
                        {" / "}
                        {emp.hora_salida ? fmtHora(emp.hora_salida) : "—"}
                      </td>
                      <td className="px-2 py-2.5">
                        <div className="flex justify-end gap-1">
                          {emp.problemas?.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 gap-1 text-amber-700"
                              onClick={() => enviarRecordatorios([emp.usuario_id])}
                            >
                              <Bell className="h-3.5 w-3.5" />
                              <span className="hidden sm:inline">Avisar</span>
                            </Button>
                          )}
                          {!esAusencia && !esNoLaborable && (
                            <>
                              <Button size="sm" variant="ghost" className="h-8 gap-1" asChild>
                                <Link to={`/admin/fichajes?usuario=${emp.usuario_id}&manual=1&fecha=${data.fecha}`}>
                                  <PenLine className="h-3.5 w-3.5" />
                                  <span className="hidden sm:inline">Registrar</span>
                                </Link>
                              </Button>
                              {puedeRegistrarAlgunTipo && (
                              <Button size="sm" variant="ghost" className="h-8 gap-1 text-indigo-700" onClick={() => abrirAusencia(emp)}>
                                <CalendarOff className="h-3.5 w-3.5" />
                                <span className="hidden sm:inline">Ausencia</span>
                              </Button>
                              )}
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="border-t border-zinc-100 bg-zinc-50/80 px-5 py-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Enviar recordatorio masivo</p>
          <Textarea
            className="mt-2 min-h-[60px] bg-white text-sm"
            placeholder="Mensaje opcional (por defecto: recuerda completar entrada y salida)"
            value={mensaje}
            onChange={(e) => setMensaje(e.target.value)}
          />
          <label className="mt-2 flex items-center gap-2 text-sm text-zinc-600">
            <Checkbox checked={enviarEmail} onCheckedChange={(v) => setEnviarEmail(!!v)} />
            Enviar también por correo electrónico (si está configurado en Ajustes)
          </label>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Button
              onClick={enviarRecordatorios}
              disabled={enviando || sel.size === 0}
              className="gap-2 bg-rose-600 hover:bg-rose-700"
              data-testid="enviar-recordatorios-btn"
            >
              <Send className="h-4 w-4" />
              Notificar a {sel.size} seleccionado(s)
            </Button>
            <Link to="/admin/informes" className="text-xs text-indigo-600 hover:underline">
              Ver balance de horas y extras →
            </Link>
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-zinc-400">
            Obligación legal: registro diario de entrada y salida, conservación 4 años, accesible a empleados e inspección.
            Los fichajes manuales del encargado quedan trazados con autor y justificación.
          </p>
        </div>
      </div>

      <RegistrarAusenciaDialog
        open={ausenciaOpen}
        onOpenChange={setAusenciaOpen}
        usuarios={usuarios}
        usuarioId={ausenciaUsuario?.usuario_id}
        fechaReferencia={data.fecha}
        asuntosPropiosActivo={asuntosPropiosActivo}
        permisosAprobacion={permisosAprobacion}
        onSuccess={cargar}
      />
    </>
  );
}
